import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@core/store'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@shared/views/LoginView.vue'),
    meta: { title: 'Login' },
  },
  {
    path: '/release-notes',
    name: 'ReleaseNotesPublic',
    component: () => import('@modules/system/views/ReleaseNotesView.vue'),
    meta: { title: 'Release Notes', public: true },
  },
  {
    path: '/',
    component: () => import('@shared/layouts/AdminLayout.vue'),
    children: [
      // 首页
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@modules/dashboard/views/DashboardView.vue'),
        meta: { title: '首页', icon: 'dashboard' },
      },
      {
        path: 'operations/tracker',
        name: 'ProductJourneyTracker',
        component: () => import('@modules/operations/views/ProductJourneyTrackerView.vue'),
        meta: { title: 'Product Journey Tracker', icon: 'search' },
      },

      // 外部信息
      {
        path: 'external/customers',
        name: 'CustomerInfo',
        component: () => import('@modules/customer/views/CustomerListView.vue'),
        meta: { title: '客户信息', icon: 'usergroup' },
      },
      {
        path: 'external/products',
        name: 'ProductInfo',
        component: () => import('@modules/customer/views/ProductListView.vue'),
        meta: { title: '产品信息', icon: 'product' },
      },
      {
        path: 'external/orders',
        name: 'OrderInfo',
        component: () => import('@modules/order/views/OrderListView.vue'),
        meta: { title: '订单信息', icon: 'filelist' },
      },
      {
        path: 'external/formulas',
        name: 'FormulaInfo',
        component: () => import('@modules/formula/views/FormulaListView.vue'),
        meta: { title: '配方信息', icon: 'file-paste' },
      },

      // 人力资源
      {
        path: 'hr/overview',
        name: 'WorkforceOverview',
        component: () => import('@modules/hr/views/WorkforceOverviewView.vue'),
        meta: { title: 'Workforce Overview', icon: 'chart-bar' },
      },
      {
        path: 'hr/personnel',
        redirect: { name: 'EmployeeDirectory' },
      },
      {
        path: 'hr/employees',
        name: 'EmployeeDirectory',
        component: () => import('@modules/hr/views/PersonnelView.vue'),
        meta: { title: 'Employee Directory', icon: 'user' },
      },
      {
        path: 'hr/employees/:id',
        name: 'EmployeeDetail',
        component: () => import('@modules/hr/views/EmployeeDetailView.vue'),
        meta: { title: 'Employee Detail', icon: 'user' },
      },
      {
        path: 'hr/shifts',
        redirect: { name: 'ShiftSetup' },
      },
      {
        path: 'hr/shifts/setup',
        name: 'ShiftSetup',
        component: () => import('@modules/hr/views/ShiftManagementView.vue'),
        meta: { title: 'Shift Templates', icon: 'calendar' },
      },
      {
        path: 'hr/shifts/coverage',
        name: 'ShiftCoverage',
        component: () => import('@modules/hr/views/ShiftManagementView.vue'),
        meta: { title: 'Shift Coverage', icon: 'calendar-2' },
      },
      {
        path: 'hr/attendance',
        name: 'AttendanceReport',
        component: () => import('@modules/hr/views/AttendanceReportView.vue'),
        meta: { title: 'Daily Attendance', icon: 'file-list' },
      },
      {
        path: 'hr/overtime',
        name: 'OvertimeManagement',
        component: () => import('@modules/hr/views/OvertimeManagementView.vue'),
        meta: { title: 'Overtime Requests', icon: 'time' },
      },
      {
        path: 'hr/leave',
        name: 'LeaveManagement',
        component: () => import('@modules/hr/views/LeaveManagementView.vue'),
        meta: { title: 'Leave Requests', icon: 'logout' },
      },
      {
        path: 'hr/approvals',
        name: 'ApprovalCenter',
        component: () => import('@modules/hr/views/ApprovalCenterView.vue'),
        meta: { title: 'Approval Center', icon: 'check-double' },
      },
      {
        path: 'hr/performance',
        name: 'Performance',
        component: () => import('@modules/hr/views/PerformanceView.vue'),
        meta: { title: 'Performance', icon: 'star' },
      },
      {
        path: 'hr/skills',
        name: 'SkillAuthorization',
        component: () => import('@modules/hr/views/SkillAuthorizationView.vue'),
        meta: { title: 'Skills & Authorization', icon: 'verified' },
      },
      {
        path: 'hr/org-structure',
        name: 'OrgStructureDashboard',
        component: () => import('@modules/hr/views/OrgStructureDashboardView.vue'),
        meta: { title: 'Organization Structure', icon: 'sitemap' },
      },

      // 系统设置
      {
        path: 'settings/menus',
        name: 'MenuManagement',
        component: () => import('@modules/system/views/MenuManagementView.vue'),
        meta: { title: '菜单管理', icon: 'menu' },
      },
      {
        path: 'settings/organization',
        name: 'OrganizationManagement',
        component: () => import('@modules/organization/views/OrganizationView.vue'),
        meta: { title: '组织管理', icon: 'organization' },
      },
      {
        path: 'settings/quality-standards',
        name: 'SettingsQualityStandards',
        component: () => import('@modules/quality/views/StandardView.vue'),
        meta: { title: '质检标准', icon: 'check-circle' },
      },
      {
        path: 'settings/processes',
        name: 'ProcessManagement',
        component: () => import('@modules/equipment/views/ProcessView.vue'),
        meta: { title: '工序管理', icon: 'flow' },
      },
      {
        path: 'settings/roles',
        name: 'RoleManagement',
        component: () => import('@modules/system/views/RoleManagementView.vue'),
        meta: { title: '角色管理', icon: 'user-setting' },
      },
      {
        path: 'settings/dict',
        name: 'DictManagement',
        component: () => import('@modules/system/views/DictManagementView.vue'),
        meta: { title: '数据字典', icon: 'book' },
      },
      {
        path: 'settings/logs',
        name: 'SystemLogs',
        component: () => import('@modules/system/views/SystemLogView.vue'),
        meta: { title: 'System Logs', icon: 'file-lines' },
      },
      {
        path: 'settings/changelog',
        name: 'ReleaseNotes',
        component: () => import('@modules/system/views/ReleaseNotesView.vue'),
        meta: { title: 'Release Notes', icon: 'book' },
      },

      // 仓储管理
      {
        path: 'warehouse/workbench',
        name: 'WarehouseWorkbench',
        component: () => import('@modules/warehouse/views/WorkbenchView.vue'),
        meta: { title: '仓储工作台', icon: 'dashboard' },
      },
      {
        path: 'warehouse/setup',
        name: 'WarehouseSetup',
        component: () => import('@modules/warehouse/views/WarehouseSetupView.vue'),
        meta: { title: '仓库设置', icon: 'setting' },
      },
      {
        path: 'warehouse/locations',
        name: 'LocationManage',
        component: () => import('@modules/warehouse/views/LocationManageView.vue'),
        meta: { title: '库位管理', icon: 'queue' },
      },
      {
        path: 'warehouse/inventory',
        name: 'Inventory',
        component: () => import('@modules/warehouse/views/InventoryListView.vue'),
        meta: { title: '库存台账', icon: 'filelist' },
      },
      {
        path: 'warehouse/semi-finished',
        name: 'SemiFinishedInventory',
        component: () => import('@modules/warehouse/views/SemiFinishedInventoryView.vue'),
        meta: { title: '半成品库存', icon: 'layers' },
      },
      {
        path: 'warehouse/inout',
        name: 'InboundOutbound',
        component: () => import('@modules/warehouse/views/InOutView.vue'),
        meta: { title: '入库出库', icon: 'swap' },
      },
      {
        path: 'warehouse/scan-inbound',
        name: 'ScanInbound',
        component: () => import('@modules/warehouse/views/ScanInboundView.vue'),
        meta: { title: '扫码操作', icon: 'qr-code' },
      },
      {
        path: 'warehouse/stock-check',
        name: 'StockCheck',
        component: () => import('@modules/warehouse/views/StockCheckView.vue'),
        meta: { title: '库存盘点', icon: 'check-circle' },
      },
      {
        path: 'warehouse/batch-trace',
        name: 'BatchTrace',
        component: () => import('@modules/warehouse/views/BatchTraceView.vue'),
        meta: { title: '批次追溯', icon: 'search' },
      },
      {
        path: 'warehouse/qrcode',
        name: 'QRCodeManage',
        component: () => import('@modules/warehouse/views/QRCodeView.vue'),
        meta: { title: '二维码管理', icon: 'qrcode' },
      },

      // 质量管理
      {
        path: 'quality/workbench',
        name: 'QualityWorkbench',
        component: () => import('@modules/quality/views/WorkbenchView.vue'),
        meta: { title: '质检工作台', icon: 'dashboard', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/list',
        name: 'QualityList',
        component: () => import('@modules/quality/views/InspectionListView.vue'),
        meta: { title: '检验单', icon: 'file-list', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/items',
        name: 'QualityItems',
        component: () => import('@modules/quality/views/QCItemListView.vue'),
        meta: { title: '检验项目', icon: 'checklist', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/standards',
        name: 'QualityStandards',
        component: () => import('@modules/quality/views/StandardView.vue'),
        meta: { title: '检验标准', icon: 'file', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/templates',
        name: 'QualityTemplates',
        component: () => import('@modules/quality/views/TemplateManageView.vue'),
        meta: { title: '检验模板', icon: 'template', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/samples',
        name: 'QualitySamples',
        component: () => import('@modules/quality/views/SampleManageView.vue'),
        meta: { title: '样本管理', icon: 'queue', permissions: ['dept:qc'] },
      },
      {
        path: 'quality/nc-records',
        name: 'NCRecords',
        component: () => import('@modules/quality/views/NCRecordView.vue'),
        meta: { title: '不合格处理', icon: 'error-circle', permissions: ['dept:qc'] },
      },

      // 设备管理
      {
        path: 'equipment/ledger',
        name: 'EquipmentLedger',
        component: () => import('@modules/equipment/views/EquipmentListView.vue'),
        meta: { title: '设备台账', icon: 'view-list' },
      },
      {
        path: 'equipment/maintenance',
        name: 'EquipmentMaintenance',
        component: () => import('@modules/equipment/views/MaintenanceView.vue'),
        meta: { title: '设备维保', icon: 'tool' },
      },

      // 生产管理
      {
        path: 'production/workbench',
        name: 'ProductionWorkbench',
        component: () => import('@modules/production/views/ProductionWorkbenchView.vue'),
        meta: { title: '生产工作台', icon: 'dashboard' },
      },
      {
        path: 'production/schedule-board',
        name: 'ProductionScheduleBoard',
        component: () => import('@modules/production/views/ProductionScheduleBoardView.vue'),
        meta: { title: 'Scheduling Board', icon: 'calendar' },
      },
      {
        path: 'production/procurement-review',
        name: 'PPICProcurementReview',
        component: () => import('@modules/production/views/PPICProcurementReviewView.vue'),
        meta: { title: 'PPIC Procurement Review', icon: 'chart' },
      },
      {
        path: 'production/po',
        name: 'ProductionOrder',
        component: () => import('@modules/production/views/ProductionOrderListView.vue'),
        meta: { title: '生产计划', icon: 'file-list' },
      },
      {
        path: 'production/mo',
        name: 'ManufacturingOrderList',
        component: () => import('@modules/production/views/ManufacturingOrderListView.vue'),
        meta: { title: '生产指令', icon: 'checklist' },
      },
      {
        path: 'production/prepare',
        name: 'ProductionPrepare',
        component: () => import('@modules/production/views/ProductionPrepareView.vue'),
        meta: { title: '生产准备', icon: 'checklist', permissions: ['production:view'] },
      },
      {
        path: 'production/operation-report',
        name: 'OperationReport',
        component: () => import('@modules/production/views/OperationReportView.vue'),
        meta: { title: '工序报工', icon: 'edit', permissions: ['production:view'] },
      },
      {
        path: 'production/first-piece',
        name: 'FirstPieceConfirm',
        component: () => import('@modules/production/views/FirstPieceConfirmView.vue'),
        meta: { title: '首件确认', icon: 'check-circle', permissions: ['production:view'] },
      },
      {
        path: 'production/exception',
        name: 'ExceptionReport',
        component: () => import('@modules/production/views/ExceptionReportView.vue'),
        meta: { title: '异常上报', icon: 'error-circle' },
      },
      {
        path: 'production/completion',
        name: 'CompletionInbound',
        component: () => import('@modules/production/views/CompletionInboundView.vue'),
        meta: { title: '完工入库', icon: 'archive' },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由全局守卫：检查登录状态和权限
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()

  // 初始化用户（从 localStorage 恢复会话）
  if (!userStore.isLoggedIn) {
    userStore.initUser()
  }

  if (to.path === '/login') {
    if (userStore.isLoggedIn) {
      next({ path: '/dashboard' })
      return
    }
    next()
    return
  }

  if (to.meta?.public) {
    next()
    return
  }

  // 未登录 → 跳转登录
  if (!userStore.isLoggedIn) {
    next({ path: '/login' })
    return
  }

  // 权限检查
  const perms: string[] = (to.meta?.permissions as string[]) || []
  if (perms.length > 0) {
    const ok = perms.some((p) => userStore.hasPermission(p))
    if (!ok) {
      next({ path: '/dashboard' })
      return
    }
  }
  next()
})

export default router
